/**
 * The Behaviour interface defines methods,
 * that any behaviour must implement to be
 * applicable in the ResponsiveBehaviour class
 */
export interface Behaviour {
    apply(): void;
    destroy(): void;
}

/**
 * The ResponsiveBehaviour class handles the application of
 * different menu behaviours for certain viewport widths
 */
export class ResponsiveBehaviour {
    protected active: Behaviour | null = null;

    /**
     * Constructor
     *
     * @param {Record<number, Behaviour>} [behaviours={}]
     */
    public constructor(protected behaviours: Record<number, Behaviour> = {}) {}

    /**
     * Method to register a behaviour
     * for a minimum viewport width
     *
     * @param {number} minWidth
     * @param {Behaviour} behaviour
     * @returns {void}
     */
    public register(minWidth: number, behaviour: Behaviour): void {
        this.behaviours[minWidth] = behaviour;
    }

    /**
     * Method to handle the reevaluation of the
     * applicable behaviour after resizing
     *
     * @returns {void}
     */
    public watch(): void {
        // Evaluate the initially applicable behaviour
        this.evaluate();

        // Reevaluate the applicable behaviour on resize events
        window.addEventListener("resize", this.evaluate);
    }

    /**
     * Method to evaluate the behaviour after resizings
     * or the initial instantiation and apply the selected
     * behaviour, should it have changed
     *
     * @returns {void}
     */
    private evaluate = (): void => {
        const applicable = Object.keys(this.behaviours)
            .map(Number)
            .filter((minWidth) => minWidth <= window.innerWidth)
            .sort((a, b) => b - a);

        if (applicable.length) {
            const behaviour = this.behaviours[applicable[0]];

            if (this.active === null) {
                this.active = behaviour;
                this.active.apply();
            } else if (this.active !== behaviour) {
                this.active.destroy();
                this.active = behaviour;
                this.active.apply();
            }
        }
    };
}
